import { useCallback } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import BodyText from 'LuxKit/Typography/BodyText'
import RadioButton from 'LuxKit/Radio/RadioButton'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import focusStyle from 'styles/utils/focusStyle'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'

const OptionItem = styled.label`
  position: relative; /* for the floating label */
  border-radius: ${props => props.theme.borderRadius.L};
  box-shadow: inset 0 0 0 var(--stroke-w, 1px) var(--stroke-colour, var(--muted));
  transition: box-shadow 200ms;
  display: grid;
  grid-template: "symbol details input" auto / min-content minmax(0, 1fr) min-content;
  gap: ${rem(16)};
  padding: ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding-block: ${rem(32)};
    padding-inline: ${rem(32)} ${rem(24)};
  }

  &.variant-default {
    --accent: ${props => props.theme.palette.neutral.surface.darkForeground};
    --muted: ${props => props.theme.palette.neutral.surface.lightForeground};
  }
  &.variant-highlight {
    --accent: ${props => props.theme.palette.highlight.primary.darkBackground};
    --muted: ${props => props.theme.palette.highlight.primary.darkBackground};
  }

  &:has(input:checked) {
    --stroke-colour: var(--accent);
    --stroke-w: 2px;
  }

  &:not(:has(input:checked)) {
    cursor: pointer;
  }

  ${mediaHoverable} {
    &:hover:not(:has(input:checked)) {
      --stroke-colour: var(--accent);
      --stroke-w: 1px;
    }
  }

  &:has(:focus-visible) {
    ${focusStyle}
  }
`

const OptionInput = styled(RadioButton)`
  grid-area: input;
  align-self: center;
`

const OptionDetails = styled(Group)`
  grid-area: details;
`

const OptionSymbol = styled(Sheet)`
  grid-area: symbol;
  display: grid;
  grid-template: auto / auto;
  place-items: center;
  height: ${rem(32)};
  width: ${rem(32)};
`

const FloatingLabel = styled(PromotionLabel)`
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  transform: translateY(-50%);
`

export interface TransferOption {
  id: string
  title: string
  subtitle: React.ReactNode
  icon: React.ReactNode
  available: boolean
  price?: number | null
  pointsEarnCalculationRequests?: Array<(App.LoyaltyPointsEarnCalculationRequest | undefined)>
  upgradeLabel?: string
  isPopular?: boolean
  transferOptions?: {
    toHotel?: App.ExperienceTransferOption
    toAirport?: App.ExperienceTransferOption
  }
}

interface Props {
  option: TransferOption
  name: string
  checked: boolean
  variant?: 'default' | 'highlight'
  onChange: (value: string) => void
}

function CheckoutLoyaltyTransferOption({
  option,
  name,
  checked,
  variant = 'default',
  onChange,
}: Props) {
  const intl = useIntl()
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const handleChange = useCallback(() => {
    if (option.available) {
      onChange(option.id)
    }
  }, [option.available, onChange, option.id])

  return (
    <OptionItem className={`variant-${variant}`} onClick={handleChange}>
      <OptionInput name={name} value={option.id} checked={checked} onChange={handleChange} />

      <OptionSymbol background={`tier-${tier}-dark`} borderRadius="round">
        {option.icon}
      </OptionSymbol>
      <OptionDetails direction="horizontal" horizontalAlign="space-between" gap={2}>
        <Group direction="vertical" gap={2}>
          <BodyText variant="medium" colour="light-foreground-alt" weight="bold">
            {option.title}
          </BodyText>
          <BodyText variant="large" colour="light-foreground-alt">
            {option.subtitle}
          </BodyText>
          {/* Mobile pricing */}
          <CSSBreakpoint max="mobile">
            {option.price && (
              <Group direction="vertical" gap={2}>
                <PriceRowPrice size="S" price={option.price} />
                {option.pointsEarnCalculationRequests && (
                  <LoyaltyPointsEarn
                    calculationRequests={option.pointsEarnCalculationRequests}
                    calculationType="exact"
                  />
                )}
              </Group>
            )}
          </CSSBreakpoint>
        </Group>

        {/* Desktop pricing */}
        <CSSBreakpoint min="tablet">
          {option.price && (
            <Group direction="vertical" gap={2}>
              <PriceRowPrice saleUnit="total" size="S" price={option.price} />
              {option.pointsEarnCalculationRequests && (
                <LoyaltyPointsEarn
                  calculationRequests={option.pointsEarnCalculationRequests}
                  calculationType="exact"
                />
              )}
            </Group>
          )}
        </CSSBreakpoint>
      </OptionDetails>

      {option.isPopular && (
        <FloatingLabel variant="premium">{intl.formatMessage({
          id: 'rkuyLCuLuwcLCYDNRCro',
          defaultMessage: 'Popular upgrade',
          description: 'Label for popular transfer upgrade option in checkout loyalty',
        })}
        </FloatingLabel>
      )}
    </OptionItem>
  )
}

export default CheckoutLoyaltyTransferOption
